import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { unlockAudio } from "@/lib/audio";
import { api, SubscriptionRequiredError } from "@/lib/api";

const KIND_LABEL = {
  morning:   { emoji: "🌅", label: "Matinée magique" },
  afternoon: { emoji: "🌞", label: "Après-midi enchanté" },
  evening:   { emoji: "🌙", label: "Soirée câline" },
};

const PERSONALITIES = [
  { id: "calm",           label: "Calme",          emoji: "🌿" },
  { id: "energetic",      label: "Énergique",      emoji: "⚡️" },
  { id: "creative",       label: "Créatif",        emoji: "🎨" },
  { id: "introvert",      label: "Réservé",        emoji: "🌙" },
  { id: "extrovert",      label: "Extraverti",     emoji: "☀️" },
  { id: "hypersensitive", label: "Hypersensible",  emoji: "💧" },
];

const GENDERS = [
  { id: "neutral", label: "Préfère ne pas dire", emoji: "🌈" },
  { id: "girl",    label: "Fille",               emoji: "👧" },
  { id: "boy",     label: "Garçon",              emoji: "👦" },
];

const OBJECTIVES = [
  { id: "motor_development",       label: "Moteur",         emoji: "⚡"  },
  { id: "cognitive",               label: "Cognitif",       emoji: "🧠" },
  { id: "creative_expression",     label: "Créatif",        emoji: "🎨" },
  { id: "language_arts",           label: "Langage",        emoji: "📚" },
  { id: "values_empathy",          label: "Valeurs",        emoji: "💛" },
  { id: "autonomy",                label: "Autonomie",      emoji: "🌱" },
  { id: "household_participation", label: "Tâches maison",  emoji: "🧺" },
  { id: "mindfulness",             label: "Pleine conscience", emoji: "🌙" },
];

function defaultObjectivesFor(age) {
  if (age <= 1)  return ["motor_development", "language_arts", "values_empathy", "mindfulness"];
  if (age <= 4)  return ["motor_development", "autonomy", "language_arts", "values_empathy"];
  if (age <= 7)  return ["motor_development", "cognitive", "creative_expression", "values_empathy", "autonomy"];
  return ["cognitive", "language_arts", "creative_expression", "autonomy", "household_participation"];
}

export default function ChildSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const kindFromUrl = searchParams.get("kind");
  const kind = ["morning", "afternoon", "evening"].includes(kindFromUrl) ? kindFromUrl : null;
  const kindMeta = kind ? KIND_LABEL[kind] : null;
  const ctaLabel = kindMeta ? `Lancer ${kindMeta.label.toLowerCase()}` : "Lancer la matinée";

  const [firstName, setFirstName] = useState("");
  const [age, setAge] = useState(5);
  const [personality, setPersonality] = useState("calm");
  const [gender, setGender] = useState("neutral");
  // Default objectives per age band — matches config/objectives.php so we
  // stay aligned with what the backend would auto-fill.
  const [objectives, setObjectives] = useState(() => defaultObjectivesFor(5));
  const [busy, setBusy] = useState(false);

  const toggleObjective = (id) =>
    setObjectives((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const submit = async (event) => {
    event.preventDefault();
    if (!firstName.trim()) {
      toast.error("Indiquez le prénom de l'enfant");
      return;
    }
    // Must be inside the tap handler — primes audio for iOS so the voice
    // + music of the very first mission actually play after the redirect.
    unlockAudio();
    setBusy(true);
    try {
      const child = await api.createChild({
        first_name: firstName.trim(),
        age: Number(age),
        personality,
        gender,
        objectives,
      });
      const journey = await api.generateJourney(child.id, kind);
      navigate(`/journey/${journey.id}`);
    } catch (err) {
      if (err instanceof SubscriptionRequiredError) {
        toast.message(err.message || "Abonnement requis pour ce parcours.");
        window.location.assign(err.detail?.billing_url || "/parent/billing?highlight=paywall");
        return;
      }
      toast.error("Oups, impossible de démarrer le parcours.");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-10">
      <Link to="/" className="mb-6 text-sm text-[#5b4b6b]">← Retour</Link>

      <header className="mb-8 space-y-2">
        <h1 className="font-[Fredoka] text-3xl font-semibold text-[#3b2a4a]">
          {kindMeta ? `${kindMeta.emoji} ${kindMeta.label}` : "Faisons connaissance ✨"}
        </h1>
        <p className="text-sm text-[#5b4b6b]">
          Neo et Lyria personnaliseront la matinée selon votre enfant.
        </p>
      </header>

      <form onSubmit={submit} className="flex flex-col gap-6">
        <div className="space-y-2">
          <Label htmlFor="first_name" className="text-[#3b2a4a]">Prénom</Label>
          <Input
            id="first_name"
            placeholder="Loulou"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="rounded-2xl border-none bg-white/80 py-6 text-base shadow-sm"
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="age" className="text-[#3b2a4a]">
            Âge ({Number(age) === 0 ? "moins d'1 an (nourrisson)" : `${age} an${Number(age) > 1 ? "s" : ""}`})
          </Label>
          <input
            id="age"
            type="range"
            min={0}
            max={12}
            value={age}
            onChange={(e) => {
              const newAge = Number(e.target.value);
              setAge(newAge);
              // Refresh the recommended objectives when the age tier changes.
              setObjectives(defaultObjectivesFor(newAge));
            }}
            className="w-full accent-[#ff8a65]"
          />
          <div className="flex justify-between text-xs text-[#7a6b8a]">
            <span>0 mois</span><span>12 ans</span>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-[#3b2a4a]">Comment se présente votre enfant ?</Label>
          <div className="grid grid-cols-3 gap-2">
            {GENDERS.map((g) => {
              const active = gender === g.id;
              return (
                <button key={g.id} type="button" onClick={() => setGender(g.id)}
                  className={[
                    "flex items-center justify-center gap-1.5 rounded-2xl px-2 py-2.5 text-xs font-medium shadow-sm transition",
                    active ? "bg-[#ff8a65] text-white" : "bg-white/80 text-[#3b2a4a] hover:bg-white",
                  ].join(" ")}>
                  <span aria-hidden>{g.emoji}</span>
                  <span>{g.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-[#3b2a4a]">
            Objectifs de la journée
            <span className="ml-1 text-xs font-normal text-[#7a6b8a]">(plusieurs possibles)</span>
          </Label>
          <div className="grid grid-cols-2 gap-2">
            {OBJECTIVES.map((o) => {
              const active = objectives.includes(o.id);
              return (
                <button key={o.id} type="button" onClick={() => toggleObjective(o.id)}
                  className={[
                    "flex items-center gap-2 rounded-2xl px-3 py-2.5 text-xs font-medium shadow-sm transition",
                    active ? "bg-[#c8b6ff] text-[#3b2a4a]" : "bg-white/80 text-[#3b2a4a] hover:bg-white",
                  ].join(" ")}>
                  <span aria-hidden>{o.emoji}</span>
                  <span>{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-[#3b2a4a]">Personnalité</Label>
          <div className="grid grid-cols-2 gap-2">
            {PERSONALITIES.map((p) => {
              const active = personality === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPersonality(p.id)}
                  className={[
                    "flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium shadow-sm transition",
                    active
                      ? "bg-[#ff8a65] text-white"
                      : "bg-white/80 text-[#3b2a4a] hover:bg-white",
                  ].join(" ")}
                >
                  <span aria-hidden>{p.emoji}</span>
                  <span>{p.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <Button
          type="submit"
          disabled={busy}
          size="lg"
          className="mt-4 rounded-full bg-[#ff8a65] py-6 text-base font-semibold text-white shadow-lg hover:bg-[#ff7043] disabled:opacity-60"
        >
          {busy ? "Préparation…" : `${ctaLabel} ✨`}
        </Button>
      </form>
    </main>
  );
}
